import React, { useState } from 'react';
import {
  Box, Typography, List, ListItem, ListItemText, IconButton, Link, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Button, Stack, CircularProgress, Tooltip,
} from '@mui/material';
import { Edit, Delete, OpenInNew } from '@mui/icons-material';
import { GiftIdea } from '../types';
import { formatBudget, formatRelative } from '../utils/formatters';

interface IdeaFormValues {
  title: string;
  url: string;
  notes: string;
  priceEstimate: string;
}

const emptyForm: IdeaFormValues = { title: '', url: '', notes: '', priceEstimate: '' };

interface GiftIdeaFormProps {
  open: boolean;
  forName: string;
  initial?: GiftIdea | null;
  saving?: boolean;
  onClose: () => void;
  onSave: (values: IdeaFormValues) => Promise<void> | void;
}

export const GiftIdeaForm: React.FC<GiftIdeaFormProps> = ({ open, forName, initial, saving, onClose, onSave }) => {
  const [values, setValues] = useState<IdeaFormValues>(emptyForm);
  const [touched, setTouched] = useState(false);

  React.useEffect(() => {
    if (open) {
      setValues(initial ? {
        title: initial.title || '',
        url: initial.url || '',
        notes: initial.notes || '',
        priceEstimate: initial.priceEstimate != null ? String(initial.priceEstimate) : '',
      } : emptyForm);
      setTouched(false);
    }
  }, [open, initial]);

  const titleError = touched && !values.title.trim();

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? 'Edit gift idea' : `Gift idea for ${forName}`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            autoFocus label="What is it?" fullWidth required value={values.title}
            onChange={(e) => setValues({ ...values, title: e.target.value })}
            onBlur={() => setTouched(true)}
            error={titleError}
            helperText={titleError ? 'Give it a short description' : 'e.g. "Running shoes, size 10, Brooks Ghost"'}
            inputProps={{ maxLength: 200 }}
          />
          <TextField
            label="Link (optional)" fullWidth value={values.url}
            onChange={(e) => setValues({ ...values, url: e.target.value })}
            placeholder="https://..."
            inputProps={{ inputMode: 'url' }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Rough price (optional)" value={values.priceEstimate}
              onChange={(e) => setValues({ ...values, priceEstimate: e.target.value })}
              placeholder="$40" sx={{ minWidth: 160 }}
              inputProps={{ inputMode: 'decimal' }}
            />
            <TextField
              label="Notes (optional)" fullWidth multiline minRows={1} value={values.notes}
              onChange={(e) => setValues({ ...values, notes: e.target.value })}
              placeholder="Color, size, where to buy, why they'd love it..."
              inputProps={{ maxLength: 2000 }}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained" disabled={saving || !values.title.trim()}
          onClick={() => { setTouched(true); if (values.title.trim()) onSave(values); }}
        >
          {saving ? <CircularProgress size={22} /> : initial ? 'Save' : 'Add idea'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface GiftIdeaListProps {
  ideas: GiftIdea[];
  myParticipantId?: string | null;
  canModerate?: boolean;
  showSubject?: boolean;
  emptyText?: string;
  onEdit?: (idea: GiftIdea) => void;
  onDelete?: (idea: GiftIdea) => void;
}

export const GiftIdeaList: React.FC<GiftIdeaListProps> = ({
  ideas, myParticipantId, canModerate, showSubject, emptyText, onEdit, onDelete,
}) => {
  if (ideas.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        {emptyText || 'No ideas yet.'}
      </Typography>
    );
  }
  return (
    <List disablePadding>
      {ideas.map((idea) => {
        const mine = idea.authorParticipantId === myParticipantId;
        const editable = mine || canModerate;
        return (
          <ListItem
            key={idea.id}
            alignItems="flex-start"
            divider
            sx={{ px: 0, pr: editable ? 9 : 0, '& .MuiListItemSecondaryAction-root': { top: 12, transform: 'none' } }}
            secondaryAction={editable && (onEdit || onDelete) ? (
              <Box sx={{ display: 'flex' }}>
                {onEdit && mine && (
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit(idea)}><Edit fontSize="small" /></IconButton></Tooltip>
                )}
                {onDelete && (
                  <Tooltip title="Delete"><IconButton size="small" onClick={() => onDelete(idea)}><Delete fontSize="small" /></IconButton></Tooltip>
                )}
              </Box>
            ) : undefined}
          >
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography component="span" sx={{ fontWeight: 600 }}>{idea.title}</Typography>
                  {idea.priceEstimate != null && <Chip size="small" label={`~${formatBudget(idea.priceEstimate)}`} />}
                  {idea.url && (
                    <Link href={idea.url} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.9em' }}>
                      Link <OpenInNew sx={{ fontSize: 14 }} />
                    </Link>
                  )}
                </Box>
              }
              secondary={
                <>
                  {idea.notes && (
                    <Typography component="span" variant="body2" display="block" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary', mt: 0.5 }}>
                      {idea.notes}
                    </Typography>
                  )}
                  <Typography component="span" variant="caption" color="text.secondary">
                    {showSubject && idea.subjectName ? `For ${idea.subjectName} · ` : ''}
                    {mine ? 'You' : idea.authorName || 'Someone'} suggested · {formatRelative(idea.createdAt)}
                  </Typography>
                </>
              }
            />
          </ListItem>
        );
      })}
    </List>
  );
};

export type { IdeaFormValues };
